import React from 'react';
import {
  SendOutlined,
  InboxOutlined,
  UserOutlined,
  TeamOutlined,
  ApartmentOutlined,
  ShopOutlined,
  AppstoreOutlined,
  SettingOutlined,
} from '@ant-design/icons';

export interface MenuItemConfig {
  key: string;
  icon: React.ReactNode;
  label: string;
  /** 仅具备这些角色时显示；未设置则所有有 KFC 权限的用户可见 */
  roles?: string[];
  children?: MenuChildConfig[];
}

export interface MenuChildConfig {
  key: string;
  label?: string;
  roles?: string[];
  /** 分隔线：仅用于左侧菜单分组，key 只需唯一 */
  type?: 'divider';
}

/** 一级菜单（从左到右顺序）及各自功能树节点 */
export const topMenuConfig: MenuItemConfig[] = [
  {
    key: '/shipments',
    icon: <SendOutlined />,
    label: '运单管理',
    children: [
      { key: '/shipments/list', label: '运单列表' },
      { key: '/shipments/handover', label: '运单看板' },
    ],
  },
  {
    key: '/warehouse',
    icon: <InboxOutlined />,
    label: '货物管理',
    children: [
      { key: '/warehouse/cartons', label: '货物列表' },
    ],
  },
  {
    key: '/customers',
    icon: <UserOutlined />,
    label: '客户管理',
    children: [
      { key: '/customers/list', label: '客户列表' },
      { key: '/customers/addresses', label: '客户地址' },
      { key: '/customers/shipments', label: '客户运单' },
    ],
  },
  {
    key: '/routes',
    icon: <ApartmentOutlined />,
    label: '线路管理',
    children: [
      { key: '/routes/list', label: '线路列表（已废弃）' },
      { key: '/routes/channels', label: '渠道列表' },
      { key: '/routes/carriers', label: '承运商列表' },
      { key: '/routes/transport-modes', label: '运输方式列表' },
      { key: '/routes/billing-weight-rules', label: '计费重计算规则' },
    ],
  },
  {
    key: '/agents',
    icon: <TeamOutlined />,
    label: '代理管理',
    children: [
      { key: '/agents/list', label: '代理列表' },
      { key: '/agents/depots', label: '代理仓库列表' },
      { key: '/agents/services', label: '代理渠道列表' },
      { key: 'agents-divider-price', type: 'divider' },
      { key: '/agents/price-query', label: '查询价格' },
      { key: '/agents/price-trend', label: '价格趋势' },
      { key: '/agents/price-data', label: '价格数据管理' },
    ],
  },
  {
    key: '/suppliers',
    icon: <ShopOutlined />,
    label: '供应商管理',
    children: [
      { key: '/suppliers/list', label: '供应商列表' },
      { key: '/suppliers/contacts', label: '供应商联系人' },
    ],
  },
  {
    key: '/products',
    icon: <AppstoreOutlined />,
    label: '产品管理',
    children: [
      { key: '/products/list', label: '产品列表' },
    ],
  },
  {
    key: '/system',
    icon: <SettingOutlined />,
    label: '系统',
    roles: ['admin', 'super_admin'],
    children: [
      { key: '/system/operation-logs', label: '操作日志', roles: ['admin', 'super_admin'] },
      { key: '/system/shipment-sla-thresholds', label: '运单时效阈值', roles: ['admin', 'super_admin'] },
      { key: '/system/shipxy-config', label: '船讯网密钥', roles: ['admin', 'super_admin'] },
      { key: '/system/llm-providers', label: '大模型', roles: ['admin', 'super_admin'] },
    ],
  },
];

/** 按角色过滤可见菜单 */
export function filterMenuByRole(
  config: MenuItemConfig[],
  role: string | undefined
): MenuItemConfig[] {
  return config
    .filter((item) => !item.roles || (role != null && item.roles.includes(role)))
    .map((item) => ({
      ...item,
      children: item.children
        ? trimDividers(
            item.children.filter(
              (child) => !child.roles || (role != null && child.roles.includes(role))
            )
          )
        : undefined,
    }))
    .filter((item) => !item.children || item.children.some((c) => c.type !== 'divider'));
}

/** 去掉首尾与连续的分隔线 */
function trimDividers(children: MenuChildConfig[]): MenuChildConfig[] {
  const out: MenuChildConfig[] = [];
  for (const c of children) {
    if (c.type === 'divider' && (out.length === 0 || out[out.length - 1].type === 'divider')) continue;
    out.push(c);
  }
  while (out.length > 0 && out[out.length - 1].type === 'divider') out.pop();
  return out;
}

/** 一级菜单的第一个可导航子页面 */
export function firstNavigableChild(item: MenuItemConfig | undefined): string | undefined {
  return item?.children?.find((c) => c.type !== 'divider')?.key;
}
